/**
 * What an attachment is, in the two words a page needs: which card, and where.
 *
 * The rule is that the *type of the file* decides, never who produced it. A
 * shell that drew a player because the producer plugin sent it would have to
 * learn the next plugin's name to draw the next player; a shell that reads
 * `mime` draws a player for anything anybody ever renders. So this is the one
 * place a media type is read, and the rest of the family switches on the answer.
 *
 * The order is deliberate: `mime` first because it is what the producer of the
 * file said it is; then `kind`, the channel's own bucket word
 * (the channel plugins' own bucket); then the extension, which is a guess and
 * is last. A file none of the three recognise is a file, which is a card too.
 *
 * Where the bytes are: the URI itself, base64url, as the name the one bytes
 * route answers to (`workbench/server/attachments.py`). There is no attachment
 * id in the contract to use instead, and this side is the only side that
 * encodes — the server decodes, checks the path is inside this Agent's data
 * directory, and refuses everything else.
 */

import { API_BASE } from "../../lib/base";

/**
 * `pa/contracts/messages.py` `Attachment`, as the chat projection sends it.
 *
 * Without the contract's `size`: a message's number is one whoever wrote it
 * remembered, so the card asks the bytes route how big the file is instead
 * (`servedSize.ts`).
 */
export interface Attachment {
  /** The channel's own bucket word. Advisory: `mime` is read first. */
  kind: string;
  uri: string;
  mime: string | null;
  name: string | null;
  /** The file's own extra facts. `poster` is the only key read here. */
  metadata: Record<string, unknown>;
}

/**
 * Which card. Named for what a person sees rather than for a media type, so
 * that these words never have to match anybody's registry.
 */
export type MediaKind = "picture" | "movie" | "sound" | "document" | "file";

/** A plugin preview needs a media category and URL, without a chat attachment. */
export interface MediaThumbnail { kind: "image" | "video"; url: string }
/** Whether a thumbnail is a moving picture: its first frame is drawn from the film itself. */
export const isFilmThumbnail = (thumbnail?: MediaThumbnail | null): boolean => thumbnail?.kind === "video";

const PICTURE_EXT = /\.(png|jpe?g|webp|gif|bmp)$/i;
const MOVIE_EXT = /\.(mp4|webm|mov|mkv)$/i;
const SOUND_EXT = /\.(mp3|m4a|wav|flac|ogg)$/i;
const DOCUMENT_EXT = /\.pdf$/i;

/** The top-level media types, and the one full type that is its own card. */
const BY_TYPE: Record<string, MediaKind> = {
  image: "picture",
  video: "movie",
  audio: "sound",
};
const PDF = "application/pdf";

/**
 * What the file picker offers when an Owner adds files to a message, as the
 * `accept` attribute takes it: the same three top-level types. A hint for the
 * picker only; the server decides from the bytes (`workbench/server/uploads.py`).
 */
export const GIVABLE_TYPES = Object.keys(BY_TYPE).map((top) => `${top}/*`).join(",");

export function mediaKind(item: Pick<Attachment, "kind"> & Partial<Pick<Attachment, "mime" | "name" | "uri">>): MediaKind {
  const mime = (item.mime || "").trim().toLowerCase();
  if (mime === PDF) return "document";
  const top = BY_TYPE[mime.split("/")[0]];
  if (top) return top;
  const bucket = BY_TYPE[(item.kind || "").trim().toLowerCase()];
  if (bucket) return bucket;
  const named = item.name || item.uri || "";
  if (PICTURE_EXT.test(named)) return "picture";
  if (MOVIE_EXT.test(named)) return "movie";
  if (SOUND_EXT.test(named)) return "sound";
  if (DOCUMENT_EXT.test(named)) return "document";
  return "file";
}

/** The word for that kind, in the language the Owner reads. Never a media type. */
export function typeWords(item: Attachment): string {
  return { picture: "图片", movie: "视频", sound: "音频", document: "文档", file: "文件" }[
    mediaKind(item)
  ];
}

/**
 * How big it is, said the way a person says it.
 *
 * `null` when the bytes route has not said — an unknown size is not zero, and
 * 「0 字节」 about a finished film is a sentence that sends somebody looking for
 * a bug.
 */
export function fileSizeWords(size: number | null | undefined): string | null {
  if (typeof size !== "number" || !Number.isFinite(size) || size <= 0) return null;
  if (size < 1024) return `${Math.round(size)} 字节`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = size / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal below ten, none above: 「1.4 MB」 and 「54 MB」 are both how a
  // person says it; 「54.3 MB」 is a measurement nobody asked for.
  const shown = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${shown} ${units[unit]}`;
}

/** The URI, as the one path segment the bytes route takes. */
export function attachmentUrl(uri: string): string {
  return `${API_BASE}/api/attachments/${encodeName(uri)}`;
}

/** A video's cover frame, when the file's own facts name one. */
export function posterUrl(item: Attachment): string | undefined {
  const poster = item.metadata?.poster;
  return typeof poster === "string" && poster.trim() ? attachmentUrl(poster) : undefined;
}

/**
 * base64url, unpadded: the same name `attachments.py` `uri_of` reads back.
 *
 * Written by hand rather than with a library because it is four lines and the
 * decoder on the other side is six; a dependency here would be a second thing
 * to keep in step with the server.
 */
function encodeName(uri: string): string {
  const bytes = new TextEncoder().encode(uri);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
